import React from 'react';
import { RouteComponentProps } from '@reach/router';
import styled from '@emotion/styled';

import { Link14 as Link } from './link';
import Lost from './lost';

import { postLogs, ILogRequest } from '../../apis/public/logs';
import { CONTACT_US_URL } from '../../constants';
import SEO from '../seo';

interface ErrorBoundaryState {
  hasError: boolean;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: clamp(20em, 40vh, 60em);
  max-width: max-content;
`;

/**
 * Error Boundary to fail quietly and allow user a path to continue
 * https://reactjs.org/docs/error-boundaries.html
 */
export default class ErrorBoundary extends React.Component<RouteComponentProps, ErrorBoundaryState> {
  constructor(props: RouteComponentProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    console.debug('getDerivedStateFromError');
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    const { location } = window ?? {};
    this.postLog({ message: 'There was an error that caused the site to crash', error, errorInfo, location });
  }

  async postLog(log: ILogRequest) {
    console.info('Attempting to send error log');

    try {
      await postLogs(log);
      console.info('Logs have been sent');
    } catch (error) {
      console.error('Error sending logs', { error });
    }
  }

  render() {
    if (this.state.hasError) {
      console.debug('attempting to show error');
      // You can render any custom fallback UI
      return (
        <Container>
          <SEO title="Something went wrong" />
          <h1>Something went wrong</h1>
          <p>We encountered an error, sorry for the inconvenience.</p>
          <p>
            {"If this isn't the first time you're seeing this, "}
            <Link to={CONTACT_US_URL}>leave&nbsp;us&nbsp;a&nbsp;message</Link>.
          </p>
          <Lost />
        </Container>
      );
    }

    return this.props.children;
  }
}
